import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminDataRequestsApi,
  RequestError,
  type DataRequestItem,
  type DataRequestStatus,
  type DataRequestType,
} from '../api/client';

type TypeFilter = 'all' | DataRequestType;
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'completed' | 'failed';

const TYPE_OPTIONS: TypeFilter[] = ['all', 'deletion', 'anonymization'];
const STATUS_OPTIONS: StatusFilter[] = [
  'all',
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'completed',
  'failed',
];

function formatDateTimeJst(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function shortUserId(id: string): string {
  if (!id) return '';
  if (id.length <= 8) return id;
  return `${id.slice(0, 8)}…`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'admin-data-requests-badge admin-data-requests-badge--pending';
    case 'approved':
      return 'admin-data-requests-badge admin-data-requests-badge--approved';
    case 'rejected':
      return 'admin-data-requests-badge admin-data-requests-badge--rejected';
    case 'completed':
      return 'admin-data-requests-badge admin-data-requests-badge--completed';
    case 'failed':
      return 'admin-data-requests-badge admin-data-requests-badge--failed';
    case 'withdrawn':
      return 'admin-data-requests-badge admin-data-requests-badge--withdrawn';
    default:
      return 'admin-data-requests-badge';
  }
}

/**
 * ADM データ申請承認ワークフロー画面（2026-05-30 課題管理表 #13）。
 * 削除/匿名化申請の一覧を表示し、status=pending のものに対して承認/却下を行う。
 * 却下時は理由（reason）を必須入力させる。
 */
export default function AdminDataRequests() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<DataRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [actionRequestId, setActionRequestId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [rowNotice, setRowNotice] = useState<Record<string, string>>({});
  /**
   * 非表示にした申請も出すか（課題 #93）。非表示は一覧から隠すだけで行は消えていない。
   * 消えたように見えると証跡が失われたと誤解されるため、いつでも確認できるようにする。
   */
  const [includeHidden, setIncludeHidden] = useState(false);
  /** 非表示の申請の ID。応答に非表示の印が無いため、既定の一覧との差分で見分ける */
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // 却下モーダルの状態
  const [rejectTarget, setRejectTarget] = useState<DataRequestItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const typeLabel = useCallback(
    (type: string): string =>
      t(`admin.dataRequests.typeLabel.${type}`, { defaultValue: type }),
    [t]
  );
  const statusLabel = useCallback(
    (status: string): string =>
      t(`admin.dataRequests.statusLabel.${status}`, { defaultValue: status }),
    [t]
  );

  const clearRowMessages = (requestId: string) => {
    setRowError((prev) => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
    setRowNotice((prev) => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  };

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const params: { type?: DataRequestType; status?: DataRequestStatus; limit?: number } = {
      limit: 100,
    };
    if (typeFilter !== 'all') params.type = typeFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    const rowsOf = (res: { data?: DataRequestItem[] } | null | undefined) =>
      Array.isArray(res?.data) ? res.data : [];
    const rowKey = (r: DataRequestItem) => `${r.type}:${r.id}`;
    (includeHidden
      ? Promise.all([
          adminDataRequestsApi.list(token, { ...params, includeHidden: true }),
          adminDataRequestsApi.list(token, params),
        ]).then(([withHidden, visible]) => {
          const visibleKeys = new Set(rowsOf(visible).map(rowKey));
          const rows = rowsOf(withHidden);
          setHiddenIds(new Set(rows.filter((r) => !visibleKeys.has(rowKey(r))).map(rowKey)));
          setList(rows);
        })
      : adminDataRequestsApi.list(token, params).then((res) => {
          setHiddenIds(new Set());
          setList(rowsOf(res));
        })
    )
      .catch((err) => {
        if (err instanceof RequestError && (err.status === 404 || err.status === 501)) {
          setError(t('admin.dataRequests.notImplemented'));
        } else {
          setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        }
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, statusFilter, typeFilter, includeHidden, t]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedList = useMemo(
    () =>
      [...list].sort((a, b) => {
        const ta = Date.parse(a.requested_at ?? '');
        const tb = Date.parse(b.requested_at ?? '');
        const av = Number.isNaN(ta) ? 0 : ta;
        const bv = Number.isNaN(tb) ? 0 : tb;
        return bv - av;
      }),
    [list]
  );

  /** 撤回・非表示の失敗を、理由の分かる文言にする。409 は状態が先に変わっている */
  const actionErrorMessage = (err: unknown): string => {
    if (err instanceof RequestError && err.status === 409) return t('admin.dataRequests.conflict');
    if (err instanceof RequestError && (err.status === 404 || err.status === 501)) {
      return t('admin.dataRequests.notImplemented');
    }
    return err instanceof Error ? err.message : t('common.error');
  };

  /** 管理者による撤回（課題 #93）。承認待ちのみ。本人が退職した後の整理を想定 */
  const handleWithdraw = async (row: DataRequestItem) => {
    const token = getToken();
    if (!token) return;
    const confirmMsg = t('admin.dataRequests.withdrawConfirm', {
      type: typeLabel(row.type),
      userId: shortUserId(row.user_id),
    });
    if (!window.confirm(confirmMsg)) return;
    clearRowMessages(row.id);
    setActionRequestId(row.id);
    try {
      await adminDataRequestsApi.withdraw(row.type as DataRequestType, row.id, token);
      setRowNotice((prev) => ({ ...prev, [row.id]: t('admin.dataRequests.withdrawDone') }));
      load();
    } catch (err) {
      setRowError((prev) => ({ ...prev, [row.id]: actionErrorMessage(err) }));
      if (err instanceof RequestError && err.status === 409) load();
    } finally {
      setActionRequestId(null);
    }
  };

  /** 一覧から隠す（課題 #93）。却下済み・撤回済みのみ。行は消えない */
  const handleHide = async (row: DataRequestItem) => {
    const token = getToken();
    if (!token) return;
    if (!window.confirm(t('admin.dataRequests.hideConfirm'))) return;
    clearRowMessages(row.id);
    setActionRequestId(row.id);
    try {
      await adminDataRequestsApi.hide(row.type as DataRequestType, row.id, token);
      setRowNotice((prev) => ({ ...prev, [row.id]: t('admin.dataRequests.hideDone') }));
      load();
    } catch (err) {
      setRowError((prev) => ({ ...prev, [row.id]: actionErrorMessage(err) }));
      if (err instanceof RequestError && err.status === 409) load();
    } finally {
      setActionRequestId(null);
    }
  };

  const handleApprove = async (row: DataRequestItem) => {
    const token = getToken();
    if (!token) return;
    const confirmMsg = t('admin.dataRequests.approveConfirm', {
      type: typeLabel(row.type),
      userId: shortUserId(row.user_id),
    });
    if (!window.confirm(confirmMsg)) return;
    clearRowMessages(row.id);
    setActionRequestId(row.id);
    try {
      await adminDataRequestsApi.approve(row.type as DataRequestType, row.id, token);
      setRowNotice((prev) => ({
        ...prev,
        [row.id]: t('admin.dataRequests.approveDone'),
      }));
      load();
    } catch (err) {
      const message =
        err instanceof RequestError && (err.status === 404 || err.status === 501)
          ? t('admin.dataRequests.notImplemented')
          : err instanceof Error
            ? err.message
            : t('common.error');
      setRowError((prev) => ({ ...prev, [row.id]: message }));
    } finally {
      setActionRequestId(null);
    }
  };

  const openRejectDialog = (row: DataRequestItem) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectError(null);
  };

  const closeRejectDialog = () => {
    if (rejecting) return;
    setRejectTarget(null);
    setRejectReason('');
    setRejectError(null);
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const token = getToken();
    if (!token) return;
    const reason = rejectReason.trim();
    if (reason.length === 0) {
      setRejectError(t('admin.dataRequests.rejectReasonRequired'));
      return;
    }
    setRejectError(null);
    setRejecting(true);
    try {
      await adminDataRequestsApi.reject(
        rejectTarget.type as DataRequestType,
        rejectTarget.id,
        { reason },
        token
      );
      setRowNotice((prev) => ({
        ...prev,
        [rejectTarget.id]: t('admin.dataRequests.rejectDone'),
      }));
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      const message =
        err instanceof RequestError && (err.status === 404 || err.status === 501)
          ? t('admin.dataRequests.notImplemented')
          : err instanceof Error
            ? err.message
            : t('common.error');
      setRejectError(message);
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="page admin-data-requests-page">
      <h1>{t('admin.dataRequests.title')}</h1>
      <p className="admin-data-requests-hint">{t('admin.dataRequests.intro')}</p>

      <div className="admin-card admin-data-requests-filters">
        <div className="admin-form-group">
          <label htmlFor="data-requests-type-filter">
            {t('admin.dataRequests.filterType')}
          </label>
          <select
            id="data-requests-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {t(`admin.dataRequests.typeFilterOption.${opt}`, { defaultValue: opt })}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-form-group">
          <label htmlFor="data-requests-status-filter">
            {t('admin.dataRequests.filterStatus')}
          </label>
          <select
            id="data-requests-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {t(`admin.dataRequests.statusFilterOption.${opt}`, { defaultValue: opt })}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-form-group admin-data-requests-include-hidden">
          <label>
            <input
              type="checkbox"
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
              data-testid="data-requests-include-hidden"
            />
            {t('admin.dataRequests.includeHidden')}
          </label>
        </div>
      </div>

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="admin-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="admin-card">
          {sortedList.length === 0 ? (
            <>
              <p className="admin-empty">{t('admin.dataRequests.empty')}</p>
              {/* 隠した申請が見えないだけで、無くなったわけではないことを伝える */}
              {!includeHidden && (
                <p className="admin-form-hint" data-testid="data-requests-hidden-hint">
                  {t('admin.dataRequests.emptyHiddenHint')}
                </p>
              )}
            </>
          ) : (
            <table className="admin-table admin-data-requests-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.dataRequests.columnRequestedAt')}</th>
                  <th scope="col">{t('admin.dataRequests.columnType')}</th>
                  <th scope="col">{t('admin.dataRequests.columnUserId')}</th>
                  <th scope="col">{t('admin.dataRequests.columnScope')}</th>
                  <th scope="col">{t('admin.dataRequests.columnStatus')}</th>
                  <th scope="col">{t('admin.dataRequests.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedList.map((row) => {
                  const isPending = row.status === 'pending';
                  const busy = actionRequestId === row.id;
                  const isHidden = hiddenIds.has(`${row.type}:${row.id}`);
                  // BE が非表示にできるのは却下済み・撤回済みのみ（承認待ちを隠すと対応漏れになる）
                  const canHide = !isHidden && (row.status === 'rejected' || row.status === 'withdrawn');
                  return (
                    <tr key={`${row.type}:${row.id}`}>
                      <td>{formatDateTimeJst(row.requested_at)}</td>
                      <td>{typeLabel(row.type)}</td>
                      <td>
                        <code
                          className="admin-data-requests-user-id"
                          title={row.user_id}
                        >
                          {shortUserId(row.user_id)}
                        </code>
                      </td>
                      <td>{row.scope}</td>
                      <td>
                        <span className={statusBadgeClass(row.status)}>
                          {statusLabel(row.status)}
                        </span>
                        {isHidden && (
                          <span
                            className="admin-data-requests-badge admin-data-requests-badge--hidden"
                            data-testid="data-request-hidden-badge"
                          >
                            {t('admin.dataRequests.hiddenBadge')}
                          </span>
                        )}
                      </td>
                      <td>
                        {isPending ? (
                          <div className="admin-data-requests-actions">
                            <button
                              type="button"
                              className="admin-btn-primary"
                              onClick={() => handleApprove(row)}
                              disabled={busy}
                            >
                              {t('admin.dataRequests.approveBtn')}
                            </button>
                            <button
                              type="button"
                              className="admin-btn-text admin-btn-text-danger"
                              onClick={() => openRejectDialog(row)}
                              disabled={busy}
                            >
                              {t('admin.dataRequests.rejectBtn')}
                            </button>
                            <button
                              type="button"
                              className="admin-btn-text"
                              onClick={() => handleWithdraw(row)}
                              disabled={busy}
                            >
                              {t('admin.dataRequests.withdrawBtn')}
                            </button>
                          </div>
                        ) : canHide ? (
                          <div className="admin-data-requests-actions">
                            <button
                              type="button"
                              className="admin-btn-text"
                              onClick={() => handleHide(row)}
                              disabled={busy}
                            >
                              {t('admin.dataRequests.hideBtn')}
                            </button>
                          </div>
                        ) : (
                          <span className="admin-data-requests-no-actions">
                            {t('admin.dataRequests.noActions')}
                          </span>
                        )}
                        {rowError[row.id] && (
                          <p className="admin-form-error admin-data-requests-row-error">
                            {rowError[row.id]}
                          </p>
                        )}
                        {rowNotice[row.id] && (
                          <p className="admin-form-success admin-data-requests-row-notice">
                            {rowNotice[row.id]}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {rejectTarget && (
        <div
          className="admin-consent-docs-dialog-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.dataRequests.rejectDialog.title')}</h3>
            <p>
              {t('admin.dataRequests.rejectDialog.intro', {
                type: typeLabel(rejectTarget.type),
                userId: shortUserId(rejectTarget.user_id),
              })}
            </p>
            <div className="admin-form-group">
              <label htmlFor="data-requests-reject-reason">
                {t('admin.dataRequests.rejectReasonLabel')}
              </label>
              <textarea
                id="data-requests-reject-reason"
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('admin.dataRequests.rejectReasonPlaceholder')}
              />
              <p className="admin-form-hint">
                {t('admin.dataRequests.rejectReasonHint')}
              </p>
            </div>
            {rejectError && <p className="admin-form-error">{rejectError}</p>}
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={closeRejectDialog}
                disabled={rejecting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleReject}
                disabled={rejecting || rejectReason.trim().length === 0}
              >
                {rejecting
                  ? t('common.loading')
                  : t('admin.dataRequests.rejectDialog.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">
          {t('common.back')} → {t('admin.backToDashboard')}
        </Link>
      </p>
    </div>
  );
}
